import { pushEvent } from '../../client/data-layer';

/**
 * Tracks when the user initiates product comparison selection in a category;
 * that is, when they click on the "Compare products" button to start selecting
 * products.
 */
export const trackComparisonSelectionStarted = () => {
  pushEvent('compareProductsSelectionStarted');
};

/**
 * Tracks when the user aborts product comparison selection in a category; that
 * is, when they click the "Compare products" button just above the product
 * list, or the "Abort product comparison" in the popup at the bottom of the
 * screen.
 * @param source compareButton = the button just above the product listing.
 *        basket = the "Abort product comparison" button in the compare popup.
 */
export const trackComparisonSelectionAborted = (
  source: AbortComparisonSelectionSource
) => {
  pushEvent('compareProductsSelectionAborted', { source });
};

/** The type of control that was used to abort product selection for comparison. */
export type AbortComparisonSelectionSource = 'compareButton' | 'basket';

/**
 * Tracks when the current product selection changes (on the category page,
 * before the comparison has been "submitted").
 * @param productCount Total number of selected products (*after* the change).
 * @param action Whether a product was added or removed.
 * @param source productCard = a product item was clicked to toggle its selected
 *        state. On the category page, it means any click on a product in the
 *        listing; on the compare page, clicking the "X" on a product.
 *        basket = a product was removed by clicking the "X" button in the popup
 *        thing.
 *        search = a product was added from the search on the compare page.
 */
export const trackComparisonSelectionChanged = (
  productCount: number,
  action: 'add' | 'remove',
  source: ChangeComparisonSelectionSource = 'productCard'
) => {
  pushEvent('compareProductsSelectionChanged', {
    productCount,
    action,
    source,
  });
};

/** The type of control that was used to select/deselect a product for comparison. */
export type ChangeComparisonSelectionSource =
  | 'productCard'
  | 'basket'
  | 'search';

/**
 * Tracks a click on any link that leads to the compare page. This includes the
 * "Compare" button in the compare popup thing.
 * @param productCount Total number of selected products.
 * @param source generic = any generic "Compare" button or similar that leads
 *        directly to a compare page. (In Amplitude we use page_type to figure
 *        out what page the link or button was on.)
 *        similarProducts = the "Compare" link near the similar products list.
 */
export const trackComparisonLinkClick = (
  productCount: number,
  linkType: 'general' | 'similarProducts' = 'general'
) => {
  pushEvent('compareProductsLinkClick', { productCount, linkType });
};

/**
 * Tracks a click on the "Show differences" checkbox.
 * @param showDifferences Whether the checkbox is checked or not.
 */
export const trackToggleShowDifferences = (showDifferences: boolean) => {
  pushEvent('compareProductsShowDifferences', { showDifferences });
};

/**
 * Tracks a click on the "Add" button on the compare page.
 */
export const trackAddProductStarted = () => {
  pushEvent('compareProductsAddProductStarted');
};

/**
 * Tracks when the user aborts the flow for adding a product to the comparison
 * on the compare page.
 */
export const trackAddProductAborted = () => {
  pushEvent('compareProductsAddProductAborted');
};
